import {
    getMiddleCenterBias,
    GridCellKind,
    measureTextCached,
    type CustomCell,
    type CustomRenderer,
    type Item,
} from '@glideapps/glide-data-grid';

const hasCellKind = (data: unknown, expectedKind: string): boolean =>
    typeof data === 'object' && data !== null && Reflect.get(data, 'kind') === expectedKind;

interface UserProfileCellData {
    readonly image?: string | null;
    readonly initial?: string;
    readonly kind: 'user-profile-cell';
    readonly name?: string;
    readonly tint: string;
}

export type UserProfileCell = CustomCell<UserProfileCellData>;

const userProfileCellRenderer: CustomRenderer<UserProfileCell> = {
    kind: GridCellKind.Custom,
    isMatch: (cell): cell is UserProfileCell => hasCellKind(cell.data, 'user-profile-cell'),
    draw: ({ col, ctx, imageLoader, rect, row, theme }, cell) => {
        const { image, initial = '', name, tint } = cell.data;
        const padding = theme.cellHorizontalPadding;
        const radius = Math.max(0, Math.min(12, rect.height / 2 - theme.cellVerticalPadding));
        const centerX = rect.x + padding + radius;
        const centerY = rect.y + rect.height / 2;

        ctx.save();
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.globalAlpha = 0.2;
        ctx.fillStyle = tint;
        ctx.fill();
        ctx.globalAlpha = 1;

        const firstCharacter = initial.at(0) ?? '';

        if (firstCharacter) {
            const avatarFont = `600 16px ${theme.fontFamily}`;
            ctx.font = avatarFont;
            ctx.fillStyle = theme.textDark;
            const metrics = measureTextCached(firstCharacter, ctx);
            ctx.fillText(firstCharacter, centerX - metrics.width / 2, centerY + getMiddleCenterBias(ctx, avatarFont));
        }

        if (image) {
            const loadedImage = imageLoader.loadOrGetImage(image, col, row);

            if (loadedImage) {
                ctx.save();
                ctx.beginPath();
                ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                ctx.clip();
                ctx.drawImage(loadedImage, centerX - radius, centerY - radius, radius * 2, radius * 2);
                ctx.restore();
            }
        }

        if (name) {
            ctx.font = theme.baseFontFull;
            ctx.fillStyle = theme.textDark;
            ctx.fillText(name, centerX + radius + padding, centerY + getMiddleCenterBias(ctx, theme));
        }

        ctx.restore();
        return true;
    },
    provideEditor: () => undefined,
    onPaste: (_value, data) => data,
};

interface SparklineCellData {
    readonly color?: string;
    readonly displayValues?: readonly (number | string)[];
    readonly graphKind?: 'area' | 'bar' | 'line';
    readonly hideAxis?: boolean;
    readonly kind: 'sparkline-cell';
    readonly values: readonly number[];
    readonly yAxis: Item;
}

export type SparklineCell = CustomCell<SparklineCellData>;

const sparklineCellRenderer: CustomRenderer<SparklineCell> = {
    kind: GridCellKind.Custom,
    isMatch: (cell): cell is SparklineCell => hasCellKind(cell.data, 'sparkline-cell'),
    needsHover: true,
    needsHoverPosition: true,
    draw: ({ ctx, hoverAmount, hoverX, rect, theme }, cell) => {
        const { color = theme.accentColor, displayValues, graphKind = 'area', hideAxis, values, yAxis } = cell.data;

        if (values.length === 0) return true;

        const [minimum, maximum] = yAxis;
        const range = maximum - minimum;
        const normalizedValues = values.map((value) =>
            range === 0 ? 0.5 : Math.min(1, Math.max(0, (value - minimum) / range)),
        );
        const padding = theme.cellHorizontalPadding;
        const x = rect.x + padding;
        const y = rect.y + 3;
        const width = Math.max(0, rect.width - padding * 2);
        const height = Math.max(0, rect.height - 6);
        const zeroY = maximum <= 0 ? y : minimum >= 0 ? y + height : y + height * (maximum / range);

        ctx.save();

        if (!hideAxis && minimum <= 0 && maximum >= 0) {
            ctx.beginPath();
            ctx.moveTo(x, zeroY);
            ctx.lineTo(x + width, zeroY);
            ctx.globalAlpha = 0.4;
            ctx.lineWidth = 1;
            ctx.strokeStyle = theme.textLight;
            ctx.stroke();
            ctx.globalAlpha = 1;
        }

        if (graphKind === 'bar') {
            const spacing = 2;
            const barWidth = Math.max(1, (width - (normalizedValues.length - 1) * spacing) / normalizedValues.length);

            ctx.beginPath();
            normalizedValues.forEach((value, index) => {
                const barX = x + index * (barWidth + spacing);
                const barY = y + height - value * height;
                ctx.rect(barX, Math.min(barY, zeroY), barWidth, Math.abs(zeroY - barY));
            });
            ctx.fillStyle = color;
            ctx.fill();
        } else {
            const plottedValues =
                normalizedValues.length === 1 ? [normalizedValues[0], normalizedValues[0]] : normalizedValues;
            const xStep = plottedValues.length > 1 ? width / (plottedValues.length - 1) : 0;
            const points = plottedValues.map((value, index) => ({
                x: x + xStep * index,
                y: y + height - value * height,
            }));

            ctx.beginPath();
            ctx.moveTo(points[0].x, points[0].y);
            points.slice(1).forEach((point) => ctx.lineTo(point.x, point.y));
            ctx.strokeStyle = color;
            ctx.lineWidth = 1 + hoverAmount * 0.5;
            ctx.stroke();

            if (graphKind === 'area') {
                ctx.lineTo(x + width, zeroY);
                ctx.lineTo(x, zeroY);
                ctx.closePath();
                ctx.globalAlpha = 0.2 + hoverAmount * 0.2;
                ctx.fillStyle = color;
                ctx.fill();
                ctx.globalAlpha = 1;
            }

            if (hoverX !== undefined && displayValues && xStep > 0) {
                const closestIndex = Math.min(
                    plottedValues.length - 1,
                    Math.max(0, Math.round((hoverX - padding) / xStep)),
                );

                ctx.beginPath();
                ctx.moveTo(x + closestIndex * xStep, rect.y + 1);
                ctx.lineTo(x + closestIndex * xStep, rect.y + rect.height);
                ctx.lineWidth = 1;
                ctx.strokeStyle = theme.textLight;
                ctx.stroke();
                ctx.font = `8px ${theme.fontFamily}`;
                ctx.fillStyle = theme.textMedium;
                ctx.textBaseline = 'top';
                ctx.fillText(
                    String(displayValues[Math.min(closestIndex, displayValues.length - 1)] ?? ''),
                    x,
                    rect.y + theme.cellVerticalPadding,
                );
            }
        }

        ctx.restore();
        return true;
    },
    provideEditor: () => undefined,
    onPaste: (_value, data) => data,
};

export const glideCustomCellRenderers = [userProfileCellRenderer, sparklineCellRenderer];
